<!-- Deploy trigger: 2026-02-09 - force deployment -->
<template>
  <v-app :theme="currentTheme" :data-drawer-open="drawer && !mobile">
    <!-- App Loading Screen - blocks DOM until settings load to prevent FOUC -->
    <div v-if="!appReady" class="app-loading">
      <!-- Error State -->
      <div v-if="appLoadError" class="app-loading__error">
        <v-icon size="64" color="error" class="app-loading__error-icon">
          mdi-alert-circle-outline
        </v-icon>
        <h2 class="app-loading__error-title">Unable to Load App</h2>
        <p class="app-loading__error-message">{{ appLoadError }}</p>
        <button
          class="app-loading__retry-btn"
          :disabled="isRetrying"
          @click="retryAppLoad"
        >
          <v-icon v-if="isRetrying" size="18" class="app-loading__retry-spinner">
            mdi-loading
          </v-icon>
          <span v-else>Try Again</span>
        </button>
      </div>
      <!-- Loading State -->
      <template v-else>
        <div class="app-loading__spinner" />
        <p class="app-loading__text">Loading...</p>
      </template>
    </div>

    <!-- Main App Content (hidden until settings load) -->
    <template v-else>
      <!-- Premium Background -->
      <div class="app-background" />

      <!-- App Container - centers everything on wide screens -->
      <div
        class="app-container"
        :class="{
          'app-container--drawer-open': drawer && !mobile,
        }"
      >
        <!-- Header (hidden on detail pages for mobile) -->
        <AppHeader
          v-if="!isDetailPage"
          @toggle-drawer="drawer = !drawer"
          @open-search="showSearch = true"
          @open-settings="showSettings = true"
          @open-discover="showMobileDiscover = true"
          @open-terms="showTermsModal = true"
          @open-privacy="showPrivacyModal = true"
        />

        <!-- Left Sidebar -->
        <AppSidebar
          v-model="drawer"
          :rail="rail"
          @open-settings="showSettings = true"
          @open-terms="showTermsModal = true"
          @open-privacy="showPrivacyModal = true"
        />

        <!-- Main Content -->
        <main
          ref="appMainRef"
          class="app-main"
          :class="{
            'app-main--drawer-open': drawer && !mobile,
            'app-main--drawer-collapsed': !drawer && !mobile,
            'app-main--has-bottom-tab': mobile && !hideBottomTabBar,
          }"
        >
          <div
            class="app-layout"
            :class="{
              'app-layout--wide': isWideLayout,
              'app-layout--has-player': hasAudioPlayer,
            }"
          >
            <!-- Center Content -->
            <div ref="centerScrollRef" class="app-layout__center">
              <router-view v-slot="{ Component, route }">
                <transition name="page-fade" mode="out-in">
                  <component :is="Component" :key="route.path" />
                </transition>
              </router-view>
            </div>

            <!-- Right Sidebar (desktop only) -->
            <div
              v-if="!isWideLayout"
              class="app-layout__right d-none d-lg-block"
            >
              <RightSidebar
                @filter-change="handleFilterChange"
                @open-settings="showSettings = true"
              />
            </div>
          </div>
        </main>
      </div>

      <!-- Bottom Tab Bar (mobile only) -->
      <BottomTabBar v-if="mobile && !hideBottomTabBar && route.name !== 'login'" />

      <!-- Settings Dialog -->
      <Teleport to="body">
        <Transition name="modal">
          <div
            v-if="showSettings"
            class="modal-overlay"
            @click="showSettings = false"
          >
            <v-theme-provider :theme="settingsModalTheme" with-background>
              <div class="modal-container settings-modal" @click.stop>
                <header class="settings-modal__header">
                  <h2 class="settings-modal__title">
                    <v-icon size="20">mdi-cog-outline</v-icon>
                    Settings
                  </h2>
                  <button
                    class="settings-modal__close"
                    @click="showSettings = false"
                  >
                    <v-icon size="20">mdi-close</v-icon>
                  </button>
                </header>

                <div class="settings-modal__content">
                  <!-- Theme Selection (only show when uiMode is 'both') -->
                  <template v-if="appSettings.uiMode.value === 'both'">
                    <section class="settings-section">
                      <h3 class="settings-section__title">Appearance</h3>
                      <p class="settings-section__desc">
                        Choose your preferred theme
                      </p>
                      <div class="settings-theme-toggle">
                        <button
                          class="settings-theme-btn"
                          :class="{
                            'settings-theme-btn--active':
                              uiStore.themeMode === 'light',
                          }"
                          @click="uiStore.setTheme('light')"
                        >
                          <v-icon size="18">mdi-white-balance-sunny</v-icon>
                          Light
                        </button>
                        <button
                          class="settings-theme-btn"
                          :class="{
                            'settings-theme-btn--active':
                              uiStore.themeMode === 'dark',
                          }"
                          @click="uiStore.setTheme('dark')"
                        >
                          <v-icon size="18">mdi-moon-waning-crescent</v-icon>
                          Dark
                        </button>
                        <button
                          class="settings-theme-btn"
                          :class="{
                            'settings-theme-btn--active':
                              uiStore.themeMode === 'system',
                          }"
                          @click="uiStore.setTheme('system')"
                        >
                          <v-icon size="18">mdi-theme-light-dark</v-icon>
                          System
                        </button>
                      </div>
                    </section>

                    <div class="settings-divider" />
                  </template>

                  <!-- Developer Token -->
                  <section class="settings-section">
                    <h3 class="settings-section__title">Developer Token</h3>
                    <p class="settings-section__desc">
                      Set a token directly for testing purposes
                    </p>
                    <div class="settings-token">
                      <input
                        v-model="developerToken"
                        type="password"
                        placeholder="Enter access token"
                        class="settings-token__input"
                      />
                      <button
                        class="settings-token__btn"
                        :disabled="!developerToken.trim() || isSettingToken"
                        @click="handleSetDeveloperToken"
                      >
                        {{ isSettingToken ? "Setting..." : "Set Token" }}
                      </button>
                    </div>
                    <div v-if="tokenError" class="settings-error">
                      <v-icon size="16">mdi-alert-circle</v-icon>
                      {{ tokenError }}
                    </div>
                  </section>

                  <div class="settings-divider" />

                  <!-- Invite Friends -->
                  <section class="settings-section">
                    <h3 class="settings-section__title">Invite Friends</h3>
                    <p class="settings-section__desc">
                      Share an invite link to bring friends to the app.
                    </p>
                    <div class="settings-token">
                      <input
                        :value="invite.inviteLink.value"
                        type="text"
                        readonly
                        class="settings-token__input"
                        aria-label="Invite link"
                      />
                      <button
                        class="settings-token__btn"
                        @click="handleCopyInvite"
                      >
                        Copy
                      </button>
                      <button
                        class="settings-token__btn"
                        @click="handleShareInvite"
                      >
                        Share
                      </button>
                    </div>
                    <div class="settings-api-url mt-3">
                      <code>Shared {{ invite.totalShares.value }} time(s)</code>
                    </div>
                  </section>

                  <div class="settings-divider" />

                  <!-- API Configuration -->
                  <section class="settings-section">
                    <h3 class="settings-section__title">API Configuration</h3>
                    <p class="settings-section__desc">
                      Base URL from environment
                    </p>
                    <div class="settings-api-url">
                      <code>{{ apiBaseUrl }}</code>
                    </div>
                  </section>
                </div>
              </div>
            </v-theme-provider>
          </div>
        </Transition>
      </Teleport>

      <!-- Mobile Search Dialog -->
      <Teleport to="body">
        <Transition name="modal">
          <div
            v-if="showSearch"
            class="modal-overlay modal-overlay--fullscreen"
            @click="showSearch = false"
          >
            <v-theme-provider :theme="currentTheme" with-background>
              <div class="search-modal" @click.stop>
                <header class="search-modal__header">
                  <button
                    class="search-modal__back"
                    @click="showSearch = false"
                  >
                    <v-icon>mdi-arrow-left</v-icon>
                  </button>
                  <input
                    v-model="searchQuery"
                    type="text"
                    :placeholder="searchPlaceholder"
                    class="search-modal__input"
                    autofocus
                    @input="handleSearchInput"
                    @keyup.enter="handleSearch"
                  />
                  <button class="search-modal__submit" @click="handleSearch">
                    <v-icon>mdi-magnify</v-icon>
                  </button>
                </header>
                <div class="search-modal__content">
                  <!-- Loading State -->
                  <div v-if="isSearching" class="search-modal__loading">
                    <v-progress-circular indeterminate size="24" width="2" />
                    <span>Searching...</span>
                  </div>

                  <!-- Suggestions -->
                  <div
                    v-else-if="searchSuggestions.length > 0"
                    class="search-modal__suggestions"
                  >
                    <button
                      v-for="suggestion in searchSuggestions"
                      :key="suggestion.id"
                      class="search-modal__suggestion"
                      @click="handleSuggestionClick(suggestion)"
                    >
                      <v-icon size="18">{{ suggestion.icon }}</v-icon>
                      <div class="search-modal__suggestion-content">
                        <span class="search-modal__suggestion-text">{{
                          suggestion.text
                        }}</span>
                        <span
                          v-if="suggestion.subtext"
                          class="search-modal__suggestion-subtext"
                        >
                          {{ suggestion.subtext }}
                        </span>
                      </div>
                    </button>
                  </div>

                  <!-- Empty Hint -->
                  <p v-else class="search-modal__hint">
                    Enter a search term and press Enter
                  </p>
                </div>
              </div>
            </v-theme-provider>
          </div>
        </Transition>
      </Teleport>

      <!-- Audio Player - Teleported to body to escape v-application__wrap's backface-visibility: hidden
           which creates a containing block and breaks position: fixed -->
      <Teleport to="body">
        <v-theme-provider :theme="currentTheme" with-background>
          <AudioPlayer ref="audioPlayerRef" />
        </v-theme-provider>
      </Teleport>

      <!-- Video Player -->
      <VideoPlayer ref="videoPlayerRef" />

      <!-- Comment Modal -->
      <CommentModal ref="commentModalRef" />

      <!-- Create Post Modal -->
      <CreatePostModal
        v-if="isCreatePostModalMounted"
        ref="createPostModalRef"
      />

      <!-- Create Song Modal -->
      <CreateSongModal
        v-if="isCreateSongModalMounted"
        ref="createSongModalRef"
        @close="closeCreateSong"
      />

      <!-- TanStack Query DevTools (gated by env var) -->
      <VueQueryDevtools v-if="showQueryDevtools" />

      <!-- PWA Install/Update Prompts -->
      <PWAPrompt />

      <!-- Push Notification Permission Prompt -->
      <NotificationPrompt />

      <!-- Update Notification Modal -->
      <UpdateNotificationModal
        v-model="showUpdateModal"
        :title="updateTitle"
        :message="updateMessage"
        :features="updateFeatures"
        :auto-reload="false"
        @update="handleAppUpdate"
        @later="handleAppUpdateLater"
      />

      <!-- Mobile Discover Sheet (trending + who to follow) -->
      <MobileDiscoverSheet v-model="showMobileDiscover" />

      <!-- Mobile Filter Sheet (content type filters) -->
      <MobileFilterSheet
        v-model="showMobileFilter"
        :selected-filter="feedFilter"
        @filter-change="handleFilterChange"
      />

      <!-- Legal Modals (Terms and Privacy) -->
      <PrivacyPolicyModal v-model="showPrivacyModal" />
      <TermsOfServiceModal v-model="showTermsModal" />

      <!-- Action Feedback Overlay -->
      <ActionFeedbackOverlay
        :show="feedback.show"
        :icon="feedback.icon"
        :text="feedback.text"
        :type="feedback.type"
      />

      <!-- Global Recording Button (for Feedback) -->
      <GlobalRecordingButton />
    </template>
  </v-app>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  watch,
  nextTick,
  type Ref,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { useTheme, useDisplay } from "vuetify";
import { useQueryClient } from "@tanstack/vue-query";
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { useAuth } from "@/composables/useAuth";
import { authService } from "@/services/authService";
import { useAppSettings } from "@/composables/useAppSettings";
import { isFirstPage } from "@/composables/useNavigationHistory";
import { useThemeColors } from "@/composables/useThemeColors";
import {
  usePusher,
  type ChatUnreadCountEvent,
  type PostProcessingCompleteEvent,
} from "@/composables/usePusher";
import { usePWA } from "@/composables/usePWA";
import { useUpdateNotification } from "@/composables/useUpdateNotification";
import { useInviteFriends } from "@/composables/useInviteFriends";
import { versionManager } from "@/services/versionManager";
import { useUiStore } from "@/stores/ui";
import { useAppModeStore } from "@/stores/appMode";
import { updateGroupInCache } from "@/queries/useMessagesQueries";
import { queryKeys } from "@/queries/queryKeys";
import { useSdk } from "@/composables/useSdk";
import { useAudioPlayer } from "@/composables/useAudioPlayer";
import { debounce } from "@/utils/debounce";
import { useNetworkStatus } from "@/composables/useNetworkStatus";
import { useDeepLinking } from "@/composables/useDeepLinking";
import AppHeader from "@/components/layout/AppHeader.vue";
import AppSidebar from "@/components/layout/AppSidebar.vue";
import RightSidebar from "@/components/layout/RightSidebar.vue";
import BottomTabBar from "@/components/layout/BottomTabBar.vue";
import AudioPlayer from "@/components/player/AudioPlayer.vue";
import VideoPlayer from "@/components/player/VideoPlayer.vue";
import CommentModal from "@/components/comments/CommentModal.vue";
import PWAPrompt from "@/components/pwa/PWAPrompt.vue";
import NotificationPrompt from "@/components/pwa/NotificationPrompt.vue";
import OfflineIndicator from "@/components/OfflineIndicator.vue";
import UpdateNotificationModal from "@/components/UpdateNotificationModal.vue";
import MobileDiscoverSheet from "@/components/mobile/MobileDiscoverSheet.vue";
import MobileFilterSheet from "@/components/mobile/MobileFilterSheet.vue";
import PrivacyPolicyModal from "@/components/modals/PrivacyPolicyModal.vue";
import TermsOfServiceModal from "@/components/modals/TermsOfServiceModal.vue";
import { useActionFeedback } from "@/composables/useActionFeedback";
import ActionFeedbackOverlay from "@/components/feedback/ActionFeedbackOverlay.vue";
import GlobalRecordingButton from "@/components/feedback/GlobalRecordingButton.vue";
import type { FeedFilter, Post } from "@/types";

// Lazy-load large modals so they don't impact startup time/memory.
const CreatePostModal = defineAsyncComponent(
  () => import("@/components/composer/CreatePostModal.vue"),
);
const CreateSongModal = defineAsyncComponent(
  () => import("@/components/composer/CreateSongModal.vue"),
);

const router = useRouter();
const route = useRoute();
const theme = useTheme();
const { mobile } = useDisplay();
const queryClient = useQueryClient();
const showQueryDevtools =
  (import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS as string | undefined)?.toLowerCase() ===
  "true";
const { initialize, setDeveloperToken, isAuthenticated, user } = useAuth();
const appSettings = useAppSettings();
const uiStore = useUiStore();
const appModeStore = useAppModeStore();
const themeColors = useThemeColors();
const { sdk } = useSdk();

// Invite Friends helpers (used in Settings modal)
const invite = useInviteFriends(user);

// Action feedback for app-level notifications
const { feedback, showFeedback } = useActionFeedback();

// Teleported modals may not inherit the Vuetify theme class from .v-application.
// Explicitly force theme when tenant UI mode is locked to a single value.
const settingsModalTheme = computed(() => {
  const mode = appSettings.uiMode.value;
  if (mode === "dark" || mode === "light") return mode;
  return theme.global.name.value;
});

// Hide AppHeader on detail pages (profile, post, etc.) to prevent conflicts with view-specific headers
// On desktop (>= 1280px), always show AppHeader even for detail pages
// Note: playlist-detail removed - always shows AppHeader for admin menu access
const isDetailPage = computed(() => {
  const detailRoutes = [
    "post-detail",
    "song-detail",
    "video-detail",
    "group-detail",
  ];
  const isDetailRoute =
    route.name && detailRoutes.includes(route.name as string);
  // On desktop, always show AppHeader (return false to show it)
  // On mobile, hide AppHeader for detail pages (return true to hide it)
  return isDetailRoute && mobile.value;
});

// Routes where BottomTabBar should be hidden on mobile
// Profile pages should show BottomTabBar for navigation
const hideBottomTabBar = computed(() => {
  const routesWithoutBottomTab = [
    "post-detail",
    "song-detail",
    "video-detail",
    "group-detail",
  ];
  const shouldHide =
    route.name && routesWithoutBottomTab.includes(route.name as string);
  return shouldHide && mobile.value;
});

// Routes that should use a wider center layout
const isWideLayout = computed(() => {
  const wideRoutes = ["playlists", "messages", "conversation"];
  return wideRoutes.includes(route.name as string);
});

// Initialize Pusher/Echo for real-time WebSocket connection
// This handles all real-time updates: notifications, chat, typing indicators, etc.
// Auto-connects when authenticated via the composable's internal watcher
const pusher = usePusher();

// Initialize PWA features (badge API, install prompt, etc.)
const { setBadge } = usePWA();

// Update notification management
const { isUpdateAvailable, updateOptions, performUpdate, deferUpdate } =
  useUpdateNotification();

// Computed properties for update modal (only show in production, not dev)
const showUpdateModal = computed(
  () => isUpdateAvailable.value && !import.meta.env.DEV,
);
const updateTitle = computed(
  () => updateOptions.value.title || "Update Available",
);
const updateMessage = computed(
  () =>
    updateOptions.value.message ||
    "A new version of the app is ready to install.",
);
const updateFeatures = computed(() => updateOptions.value.features || []);

// Handle update actions
function handleAppUpdate() {
  performUpdate();
}

function handleAppUpdateLater() {
  deferUpdate();
}

// Provide pusher to child components (for notifications, chat, typing, etc.)
provide("pusher", pusher);

// Provide app settings to child components
provide("appSettings", appSettings);

// Provide the main scroll container so virtualized lists can attach to it
const centerScrollRef = ref<HTMLElement | null>(null);
provide("appScrollContainer", centerScrollRef);

// App ready state - blocks DOM until settings load to prevent FOUC
const appReady = ref(false);

// App loading error state - shown when settings/branding fail to load
const appLoadError = ref<string | null>(null);
const isRetrying = ref(false);

// UI State
const drawer = ref(!mobile.value); // Start open on desktop, closed on mobile
const rail = ref(false);
const showSettings = ref(false);
const showSearch = ref(false);
const showMobileDiscover = ref(false);
const showMobileFilter = ref(false);
const showTermsModal = ref(false);
const showPrivacyModal = ref(false);
const searchQuery = ref("");
const isSearching = ref(false);

// Search placeholder
const searchPlaceholder = computed(() => "Search posts, songs and users");

// Search suggestion types
interface SearchSuggestion {
  id: string;
  type: "user" | "hashtag" | "query";
  text: string;
  subtext?: string;
  icon: string;
  data?: any;
}

const searchSuggestions = ref<SearchSuggestion[]>([]);

// Sync drawer state with screen size
watch(mobile, (isMobile) => {
  // Open drawer on desktop, close on mobile
  drawer.value = !isMobile;
});

// Close drawer when search opens (mobile only)
watch(showSearch, (isSearchOpen) => {
  if (isSearchOpen && mobile.value) {
    drawer.value = false;
  }
});

// Theme - derived from UI store
const currentTheme = computed(() => uiStore.effectiveTheme);

// Developer token
const developerToken = ref("");
const isSettingToken = ref(false);
const tokenError = ref("");

// API base URL (read-only, from env)
import { resolveApiBaseUrl } from "@/utils/env";
const apiBaseUrl = resolveApiBaseUrl();

// Keep logs quiet in production/hot paths
const DEBUG_LOGS = import.meta.env.DEV;

async function handleCopyInvite() {
  const ok = await invite.copyInviteLink();
  if (ok) {
    showFeedback("mdi-check", "Link copied", "success");
  } else {
    showFeedback("mdi-alert-circle-outline", "Copy failed", "error");
  }
}

async function handleShareInvite() {
  const ok = await invite.shareInviteLink();
  if (ok) showFeedback("mdi-share", "Invite sent", "success");
}

// Feed filter (provided to child components)
const feedFilter = ref<FeedFilter>("all");
provide("feedFilter", feedFilter);

// Method to open mobile filter sheet (provided to child components)
function openMobileFilterSheet() {
  showMobileFilter.value = true;
}
provide("openMobileFilterSheet", openMobileFilterSheet);

// Audio player ref
const audioPlayerRef = ref<InstanceType<typeof AudioPlayer> | null>(null);

// Use the audio player composable for reactive state
const {
  isPlaying: audioIsPlaying,
  currentTrack: audioCurrentTrack,
  setRepeatMode,
} = useAudioPlayer();

// Current track ID (provided to child components for highlighting)
// Returns the current track ID regardless of playing/paused state
const currentPlayingId = computed(() => {
  const track = audioCurrentTrack.value;
  return track?.ulid || track?.id || null;
});
provide("currentPlayingId", currentPlayingId);

// Audio playing state (provided to child components)
// Use the singleton ref directly for proper reactivity
provide("isAudioPlaying", audioIsPlaying);

// Check if audio player is visible (has a track loaded)
const hasAudioPlayer = computed(() => !!audioCurrentTrack.value);

// Play audio function (provided to child components)
function playAudio(post: Post, queue?: Post[]) {
  audioPlayerRef.value?.play(post, queue);
}
provide("playAudio", playAudio);

// Toggle audio playback (pause/resume)
function toggleAudio() {
  audioPlayerRef.value?.togglePlay();
}
provide("toggleAudio", toggleAudio);

// Set repeat mode (provided for featured playlists/radio stations)
provide("setRepeatMode", setRepeatMode);

// Video player ref
const videoPlayerRef = ref<InstanceType<typeof VideoPlayer> | null>(null);

// Play video function (provided to child components)
function playVideo(post: Post) {
  videoPlayerRef.value?.open(post);
}
provide("playVideo", playVideo);

// Comment modal ref
const commentModalRef = ref<InstanceType<typeof CommentModal> | null>(null);

// Open comments function (provided to child components)
// Anyone can view comments - auth check moved to submitComment in CommentModal
function openComments(post: Post) {
  commentModalRef.value?.open(post);
}
provide("openComments", openComments);

// Create post modal ref
interface CreatePostModalHandle {
  open: () => void;
  close: () => void;
  isOpen: Ref<boolean>;
}
const isCreatePostModalMounted = ref(false);
const createPostModalRef = ref<CreatePostModalHandle | null>(null);

// Open create post modal (provided to child components)
function openCreatePost() {
  // Check if authenticated first
  if (!authService.isAuthenticated()) {
    router.push({
      name: "login",
      query: { redirect: router.currentRoute.value.fullPath },
    });
    return;
  }

  // Mount on demand, then open.
  if (!isCreatePostModalMounted.value) {
    isCreatePostModalMounted.value = true;
  }
  nextTick(() => {
    createPostModalRef.value?.open();
  });
}
provide("openCreatePost", openCreatePost);

// Create song modal ref
interface OpenSongOptions {
  genre?: string;
  enterWsom?: boolean;
  wsomEventId?: number;
}
interface CreateSongModalHandle {
  open: (options?: OpenSongOptions) => void;
  close: () => void;
  isOpen: Ref<boolean>;
}
const isCreateSongModalMounted = ref(false);
const createSongModalRef = ref<CreateSongModalHandle | null>(null);
const pendingOpenSongModal = ref(false);
const pendingOpenSongOptions = ref<OpenSongOptions | undefined>(undefined);

// Watch for when the async component finishes mounting
watch(createSongModalRef, (newRef) => {
  if (newRef?.open && pendingOpenSongModal.value) {
    pendingOpenSongModal.value = false;
    newRef.open(pendingOpenSongOptions.value);
    pendingOpenSongOptions.value = undefined;
    if (DEBUG_LOGS) console.log("[App] Modal open() called after async mount");
  }
});

// Open create song modal directly (provided to child components)
function openCreateSong(options?: OpenSongOptions) {
  // Check if authenticated first
  if (!authService.isAuthenticated()) {
    router.push({
      name: "login",
      query: { redirect: "/music/upload" },
    });
    return;
  }

  if (DEBUG_LOGS) {
    console.log("[App] Opening create song modal directly (no routing)");
  }

  // If ref is already available, open directly
  if (createSongModalRef.value?.open) {
    createSongModalRef.value.open(options);
    if (DEBUG_LOGS) console.log("[App] Modal open() called successfully");
    return;
  }

  // Mount on demand and set pending flag
  pendingOpenSongModal.value = true;
  pendingOpenSongOptions.value = options;
  if (!isCreateSongModalMounted.value) {
    isCreateSongModalMounted.value = true;
  }
}
provide("openCreateSong", openCreateSong);

function closeCreateSong() {
  if (DEBUG_LOGS)
    console.log("[App] Closing upload modal (no routing changes)");
  // Modal simply closes - no routing changes needed
}

function handleFilterChange(filter: FeedFilter) {
  feedFilter.value = filter;
}

// Handle search
function handleSearch() {
  if (searchQuery.value.trim()) {
    router.push({ path: "/search", query: { q: searchQuery.value.trim() } });
    showSearch.value = false;
    searchQuery.value = "";
    searchSuggestions.value = [];
  }
}

// Fetch search suggestions
async function fetchSearchSuggestions() {
  const query = searchQuery.value.trim();
  if (query.length < 2) {
    searchSuggestions.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;
  const newSuggestions: SearchSuggestion[] = [];

  try {
    // Fetch users, hashtags, and posts in parallel
    const [usersResult, hashtagsResult, postsResult] = await Promise.all([
      sdk.searchUsersAutocomplete(query).catch((err) => {
        console.error("[App] User autocomplete failed:", err);
        return { data: [] };
      }),
      sdk.searchHashtags(query).catch((err) => {
        console.error("[App] Hashtag search failed:", err);
        return { data: { items: [] } };
      }),
      sdk.searchPosts(query).catch((err) => {
        console.error("[App] Post search failed:", err);
        return { data: { items: [] } };
      }),
    ]);

    // Null-safe API response handling
    const safeUsersResult = usersResult || { data: [] };
    const safeHashtagsResult = hashtagsResult || { data: { items: [] } };
    const safePostsResult = postsResult || { data: { items: [] } };

    // Add "Search all" query suggestion first
    if (query && !query.startsWith("#") && !query.startsWith("@")) {
      newSuggestions.push({
        id: `query-${query}`,
        type: "query",
        text: `Search for "${query}"`,
        subtext: "See all results",
        icon: "mdi-magnify",
      });
    }

    // Add post/content suggestions
    const postsData = !Array.isArray(safePostsResult.data)
      ? safePostsResult.data
      : { items: [] };
    const posts = postsData?.items || [];
    for (const post of posts.slice(0, 3)) {
      if (post && post.ulid) {
        const postText = (post as any).body || (post as any).title || "Post";
        const truncated =
          postText.length > 50 ? postText.substring(0, 50) + "..." : postText;
        newSuggestions.push({
          id: `post-${post.ulid}`,
          type: "query" as const,
          text: truncated,
          subtext: `By @${(post as any).username || "unknown"}`,
          icon: "mdi-post-outline",
          data: post,
        });
      }
    }

    // Add user suggestions (up to 3)
    const users = safeUsersResult.data || [];
    for (const user of users.slice(0, 3)) {
      if (user && user.ulid) {
        newSuggestions.push({
          id: `user-${user.ulid}`,
          type: "user",
          text: user.name || user.displayName || user.username || "Unknown",
          subtext: `@${user.username || "unknown"}`,
          icon: "mdi-account-outline",
          data: user,
        });
      }
    }

    // Add hashtag suggestions (up to 3)
    const hashtags = safeHashtagsResult.data?.items || [];
    for (const hashtag of hashtags.slice(0, 3)) {
      if (hashtag && hashtag.name) {
        newSuggestions.push({
          id: `hashtag-${hashtag.name}`,
          type: "hashtag",
          text: `#${hashtag.name}`,
          icon: "mdi-pound",
          data: hashtag,
        });
      }
    }

    searchSuggestions.value = newSuggestions;
  } catch (error) {
    console.error("[App] Failed to fetch suggestions:", error);
  } finally {
    isSearching.value = false;
  }
}

// Debounced search suggestions
const debouncedFetchSuggestions = debounce(() => {
  fetchSearchSuggestions();
}, 300);

// Handle search input changes
function handleSearchInput() {
  if (searchQuery.value.trim().length >= 2) {
    debouncedFetchSuggestions();
  } else {
    searchSuggestions.value = [];
    isSearching.value = false;
  }
}

// Handle suggestion click
function handleSuggestionClick(suggestion: SearchSuggestion) {
  if (suggestion.type === "user") {
    const user = suggestion.data;
    router.push(`/profile/${user.username}`);
    showSearch.value = false;
    searchQuery.value = "";
    searchSuggestions.value = [];
    return;
  }

  if (suggestion.type === "hashtag") {
    const hashtag = suggestion.data;
    router.push({ path: "/search", query: { q: `#${hashtag.name}` } });
    showSearch.value = false;
    searchQuery.value = "";
    searchSuggestions.value = [];
    return;
  }

  // Check if it's an audio/song result
  if (suggestion.id.startsWith("audio-") && suggestion.data) {
    const audio = suggestion.data;
    router.push(`/song/${audio.ulid}`);
    showSearch.value = false;
    searchQuery.value = "";
    searchSuggestions.value = [];
    return;
  }

  // Check if it's a post result
  if (suggestion.id.startsWith("post-") && suggestion.data) {
    const post = suggestion.data;
    router.push(`/post/${post.ulid}`);
    showSearch.value = false;
    searchQuery.value = "";
    searchSuggestions.value = [];
    return;
  }

  // Query type - just search
  handleSearch();
}

// Handle developer token setting
async function handleSetDeveloperToken() {
  if (!developerToken.value.trim()) return;

  isSettingToken.value = true;
  tokenError.value = "";

  try {
    await setDeveloperToken(developerToken.value.trim());
    showFeedback("mdi-check", "Token set", "success");
    showSettings.value = false;
    developerToken.value = "";
  } catch (err) {
    tokenError.value =
      err instanceof Error ? err.message : "Failed to set token";
  } finally {
    isSettingToken.value = false;
  }
}

// Watch theme changes from UI store
watch(
  () => uiStore.effectiveTheme,
  (newTheme) => {
    // @ts-ignore - Vuetify's theme.change() is not in types but works at runtime
    theme.change?.(newTheme);
  },
);

// Watch for uiMode changes from settings to force theme when locked
watch(
  () => appSettings.uiMode.value,
  (mode) => {
    if (mode === "light" || mode === "dark") {
      uiStore.setTheme(mode);
    }
  },
  { immediate: true },
);

// Handle real-time chat unread count updates
function handleChatUnreadUpdate(data: ChatUnreadCountEvent) {
  if (DEBUG_LOGS) console.log("[App] Received chat unread count update:", data);

  // Update the specific group's unread count in cache
  updateGroupInCache(queryClient, data.conversationUlid, {
    unreadCount: data.unreadCount,
  });
}

// Handle post processing complete events (background post creation completed)
function handlePostProcessingComplete(data: PostProcessingCompleteEvent) {
  if (DEBUG_LOGS)
    console.log(
      "[App] Post processing complete, invalidating all queries:",
      data,
    );

  // The PostProcessingComplete event is now broadcast AFTER InvalidatePostCacheJob completes,
  // so we can immediately invalidate queries without waiting for cache to clear
  // Invalidate ALL TanStack queries to ensure the new post/song appears everywhere
  Promise.all([
    // Invalidate all feed queries (covers forYou, following, trending, music, timeline, etc.)
    queryClient.invalidateQueries({
      queryKey: queryKeys.feed.all,
      refetchType: "all",
    }),
    // Invalidate all posts queries (covers lists, detail, comments, etc.)
    queryClient.invalidateQueries({
      queryKey: queryKeys.posts.all,
      refetchType: "all",
    }),
    // Invalidate all profile queries (covers user profile, posts, songs, likes, etc.)
    queryClient.invalidateQueries({
      queryKey: queryKeys.profile.all,
      refetchType: "all",
    }),
    // Invalidate all user queries (covers profile, followers, following, etc.)
    queryClient.invalidateQueries({
      queryKey: queryKeys.users.all,
      refetchType: "all",
    }),
    // Invalidate all trending queries
    queryClient.invalidateQueries({
      queryKey: queryKeys.trending.all,
      refetchType: "all",
    }),
    // Invalidate all search queries
    queryClient.invalidateQueries({
      queryKey: queryKeys.search.all,
      refetchType: "all",
    }),
  ]).catch((err) => {
    console.error(
      "[App] Error invalidating queries after post processing:",
      err,
    );
  });
}

// Track current user ULID for channel subscription
let currentUserUlid: string | null = null;

// Watch auth state to connect/disconnect WebSocket notifications and user channel
watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      // Note: Custom WebSocket notifications disabled - using Pusher/Echo instead
      // notifications.connect()

      // Subscribe to user's private channel for chat unread updates and post processing complete events
      // Wait for user data to be available
      const userUlid = user.value?.ulid;
      if (userUlid) {
        currentUserUlid = userUlid;
        pusher.subscribeToUserChannel(userUlid, handleChatUnreadUpdate);
        pusher.subscribeToPostProcessingComplete(
          userUlid,
          handlePostProcessingComplete,
        );
      }
    } else {
      // Disconnect when user logs out
      pusher.disconnect();

      // Unsubscribe from user channel
      if (currentUserUlid) {
        pusher.unsubscribeFromUserChannel(currentUserUlid);
        currentUserUlid = null;
      }
    }
  },
  { immediate: true },
);

// Also watch for user data changes (in case user loads after auth)
watch(user, (newUser) => {
  if (
    isAuthenticated.value &&
    newUser?.ulid &&
    newUser.ulid !== currentUserUlid
  ) {
    // Unsubscribe from old channel if needed
    if (currentUserUlid) {
      pusher.unsubscribeFromUserChannel(currentUserUlid);
    }
    // Subscribe to new channel
    currentUserUlid = newUser.ulid;
    pusher.subscribeToUserChannel(newUser.ulid, handleChatUnreadUpdate);
    pusher.subscribeToPostProcessingComplete(
      newUser.ulid,
      handlePostProcessingComplete,
    );
  }
});

// Main content ref
const appMainRef = ref<HTMLElement | null>(null);

// Cleanup function refs (set in onMounted, called in onUnmounted)
const cleanupNetworkRef = ref<(() => void) | null>(null);
const cleanupDeepLinkingRef = ref<(() => void) | null>(null);
const cleanupBrowserChromeRef = ref<(() => void) | null>(null);

// Initialize composables at top level (required for inject() to work)
const networkStatus = useNetworkStatus();
const deepLinking = useDeepLinking();

// Global popstate handler - navigates to home when user lands directly on a page
// and presses back button (no history to go back to)
function handleGlobalPopState() {
  // Check if player or signup modal is handling this popstate
  if ((window as any).__playerHandlingPopstate || (window as any).__signupModalHandlingPopstate) {
    return;
  }

  // Check if we're now at a "first page" state after the popstate
  // This happens when user landed directly on a deep link and pressed back
  if (isFirstPage()) {
    // Navigate to home instead of leaving the app
    // Use replace to avoid creating new history entry
    router.replace("/");
  }
}

/**
 * Load essential app configuration (settings, theme, auth)
 * This must succeed for the app to function
 */
async function loadAppConfiguration(): Promise<void> {
  // Load app settings (reactions, genres, badges, UI settings)
  await appSettings.loadSettings();

  // Check if settings loaded successfully - if error occurred, throw
  if (appSettings.error.value) {
    throw new Error(appSettings.error.value);
  }

  // Check if we got essential settings data
  // siteType being empty could indicate settings didn't load properly
  // but we don't require it - some tenants may not have it configured

  // Update document title with site name from settings
  const siteName = appSettings.uiSettings.value?.name;
  if (siteName) {
    document.title = siteName;
    // Also update Apple meta tag for PWA
    const appleTitleEl = document.getElementById("apple-title");
    if (appleTitleEl) {
      appleTitleEl.setAttribute("content", siteName);
    }
  }

  // Apply theme colors from API settings to Vuetify
  // Call directly with the loaded settings
  if (
    appSettings.uiSettings.value &&
    Object.keys(appSettings.uiSettings.value).length > 0
  ) {
    if (DEBUG_LOGS)
      console.log("[App] Applying theme colors directly after settings load");
    await themeColors.applyAllColors(appSettings.uiSettings.value);
  }
  // Also set up watchers for future changes
  themeColors.initializeThemeColors();

  // Initialize auth BEFORE showing app to ensure badges are loaded
  await initialize();
}

/**
 * Retry loading app configuration after an error
 */
async function retryAppLoad() {
  isRetrying.value = true;
  appLoadError.value = null;

  try {
    // Invalidate cache to force fresh fetch
    appSettings.invalidateCache();

    await loadAppConfiguration();

    // Success - show the app
    appReady.value = true;
  } catch (error) {
    console.error("[App] Retry failed:", error);
    appLoadError.value = error instanceof Error
      ? error.message
      : "Failed to load app configuration. Please check your internet connection and try again.";
  } finally {
    isRetrying.value = false;
  }
}

// Initialize on mount
onMounted(async () => {
  // Initialize UI store (theme, sidebar state)
  uiStore.initializeTheme();
  uiStore.initializeSidebar();

  // Apply saved theme to Vuetify
  // @ts-ignore - Vuetify's theme.change() is not in types but works at runtime
  theme.change?.(uiStore.effectiveTheme);

  // Load essential app configuration with error handling
  try {
    await loadAppConfiguration();

    // App is ready - settings loaded, theme colors applied, and auth initialized
    appReady.value = true;
  } catch (error) {
    console.error("[App] Failed to load app configuration:", error);
    appLoadError.value = error instanceof Error
      ? error.message
      : "Failed to load app configuration. Please check your internet connection and try again.";
    // Don't set appReady - keep showing loading/error screen
    return;
  }

  // Initialize network status monitoring (composable initialized at top level)
  cleanupNetworkRef.value = networkStatus.initialize();

  // Initialize deep linking (native platforms only, composable initialized at top level)
  cleanupDeepLinkingRef.value = deepLinking.initialize();

  // Mobile browser chrome detection and compensation (iOS Safari & Android Chrome)
  // Calculate browser chrome height (difference between 100vh and 100dvh)
  // and set CSS variable for positioning components above the bottom tab bar
  if (typeof window !== "undefined") {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;

    // Apply browser chrome compensation on mobile browsers (iOS Safari & Android Chrome)
    // but not in standalone/PWA mode where browser chrome doesn't exist
    if ((isIOS || isAndroid) && !isStandalone && window.visualViewport) {
      function updateBrowserChromeHeight() {
        const vh = window.innerHeight;
        const dvh = window.visualViewport?.height || window.innerHeight;
        const chromeHeight = Math.max(0, vh - dvh);
        document.documentElement.style.setProperty(
          "--mobile-browser-chrome-height",
          `${chromeHeight}px`
        );
      }

      // Update on mount and when viewport changes (browser chrome visibility)
      updateBrowserChromeHeight();
      window.visualViewport?.addEventListener("resize", updateBrowserChromeHeight);
      window.addEventListener("resize", updateBrowserChromeHeight);

      // Store cleanup function (called in top-level onUnmounted)
      cleanupBrowserChromeRef.value = () => {
        window.visualViewport?.removeEventListener("resize", updateBrowserChromeHeight);
        window.removeEventListener("resize", updateBrowserChromeHeight);
        document.documentElement.style.removeProperty("--mobile-browser-chrome-height");
      };
    }
  }

  // Initialize version manager - checks for updates and clears caches if needed
  try {
    const versionChanged = await versionManager.initialize();
    if (versionChanged) {
      if (DEBUG_LOGS) {
        console.log(
          "[App] Version changed - caches cleared, hard reload recommended",
        );
      }
      // On iOS (and some Safari/WebView contexts), the app can keep running old
      // JS while the server/CDN now serves a new build. That often manifests as
      // "navigation doesn't work" until a hard refresh because lazy-loaded route
      // chunks no longer exist at the old URLs.
      //
      // When we detect a version change, force a one-time reload to guarantee the
      // new app shell + route chunks are loaded.
      const reloadGuardKey = "app_version_reload_guard";
      const lastReloadAt = sessionStorage.getItem(reloadGuardKey);
      const lastReloadMs = lastReloadAt ? Number.parseInt(lastReloadAt, 10) : 0;

      // Prevent reload loops in case of unexpected conditions.
      if (!lastReloadMs || Number.isNaN(lastReloadMs) || Date.now() - lastReloadMs > 60_000) {
        sessionStorage.setItem(reloadGuardKey, Date.now().toString());
        window.location.reload();
      }
    }
  } catch (error) {
    console.error("[App] Failed to initialize version manager:", error);
  }

  // Listen for push notification clicks from service worker (deep linking)
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onServiceWorkerMessage);
  }

  // Global popstate handler for browser back button
  // Navigates to home when there's no history to go back to (first page scenario)
  window.addEventListener("popstate", handleGlobalPopState);
});

function onServiceWorkerMessage(event: MessageEvent) {
  if (event.data?.type === "NOTIFICATION_CLICK" && event.data?.url) {
    if (DEBUG_LOGS) {
      console.log("[App] Notification click - navigating to:", event.data.url);
    }
    router.push(event.data.url);
  }
}

onUnmounted(() => {
  // Clean up global popstate listener
  window.removeEventListener("popstate", handleGlobalPopState);

  // Clean up service worker message listener (helps dev/HMR and test environments)
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.removeEventListener(
      "message",
      onServiceWorkerMessage,
    );
  }

  // Clean up network status monitoring
  if (cleanupNetworkRef.value) {
    cleanupNetworkRef.value();
  }

  // Clean up deep linking
  if (cleanupDeepLinkingRef.value) {
    cleanupDeepLinkingRef.value();
  }

  // Clean up browser chrome height monitoring
  if (cleanupBrowserChromeRef.value) {
    cleanupBrowserChromeRef.value();
  }
});
</script>

<style lang="scss">
// Define layout constants as CSS custom properties on the app root
// These are used by both header and main content for alignment
.v-application {
  --sidebar-width: 260px;
  --sidebar-collapsed-width: 72px;
  --content-max-width: 1100px; // center (750px) + right sidebar (350px)
  --app-max-width: 1380px; // Centered layout max width (sidebar + content)
}

// App container - wraps header, sidebar, and main content
// Centers everything on wide screens
.app-container {
  position: relative;
  width: 100%;
  max-width: var(--app-max-width);
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100dvh;

  // When drawer is open, the container constrains all elements
  &--drawer-open {
    // Header, sidebar, and main all live within this container
    // Their positions are relative to this container now
  }
}

// App main content area
// margin-top accounts for fixed header height + safe area inset (notch/Dynamic Island)
// NO padding-top needed since margin handles the offset
.app-main {
  // Define header height as CSS variable for consistency
  // Desktop: 64px, Mobile: 56px (smaller header)
  --header-height: calc(64px + env(safe-area-inset-top, 0px));
  --header-height-mobile: calc(56px + env(safe-area-inset-top, 0px));

  padding-top: 0;
  padding-left: 0;
  // Let content dictate height, but ensure we at least fill the viewport minus the header
  min-height: calc(100vh - var(--header-height));
  min-height: calc(
    100dvh - var(--header-height)
  ); // Dynamic viewport height - accounts for iOS Safari toolbar
  height: auto;
  margin-top: var(--header-height); // Account for header + notch/Dynamic Island
  overflow: visible;
  background: transparent;
  transition:
    padding-left 0.3s var(--ease-out-expo),
    margin-top 0.3s var(--ease-out-expo);

  // When drawer is open on desktop, add left padding
  &--drawer-open {
    padding-left: var(--sidebar-width);
  }

  // When drawer is collapsed on desktop, add smaller left padding for icon sidebar
  &--drawer-collapsed {
    padding-left: var(--sidebar-collapsed-width);
  }

  // CRM route - full width, no sidebar padding (CRM has its own layout)
  // The CRM manages its own sidebar + header, so the outer shell must
  // fill exactly the viewport and never scroll.
  &--crm {
    position: relative;
    z-index: var(--z-base-content, 2);
    padding-left: 0;
    margin-top: 0;
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }

  // When bottom tab bar is visible (mobile), reduce height to account for it
  // Also use smaller mobile header height
  &--has-bottom-tab {
    --header-height: var(--header-height-mobile);
    min-height: calc(
      100vh - var(--header-height-mobile) -
        56px - env(safe-area-inset-bottom, 0px)
    );
    min-height: calc(
      100dvh - var(--header-height-mobile) -
        56px - env(safe-area-inset-bottom, 0px)
    );
    height: auto;
    margin-top: var(--header-height-mobile);
  }

  // iOS PWA / standalone mode - ensure proper safe area handling
  @supports (padding-top: env(safe-area-inset-top)) {
    @media (display-mode: standalone) {
      --header-height: calc(64px + env(safe-area-inset-top, 47px));
    }
  }
}

// Three-column layout
.app-layout {
  display: flex;
  // Max width is now handled by .app-container wrapper
  // No need for max-width or margin: 0 auto here
  height: auto;
  min-height: 100%;

  &__center {
    flex: 1;
    min-width: 0;
    max-width: 750px;
    height: auto;
    min-height: 100%;
    overflow-y: visible;
    overscroll-behavior-y: contain; // Prevent page refresh on PTR
    -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS
    border-left: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    border-right: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    background: rgba(var(--v-theme-surface), 0.3);
    // Add bottom safe area padding for iOS home indicator
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  &__right {
    width: 350px;
    flex-shrink: 0;
    position: sticky;
    top: var(--header-height);
    align-self: flex-start;
    max-height: calc(100vh - var(--header-height));
    overflow-y: auto;
  }

  // Wide layout mode (for playlists, etc.)
  &--wide {
    max-width: 100%;

    .app-layout__center {
      max-width: 100%;
      border-right: none;
    }
  }

  // Add padding when audio player is visible
  // Player height varies by breakpoint:
  // - Desktop (>1700px): 88px fixed
  // - Mid-desktop (<=1700px): 56px compact single-row
  // - Mobile (<=768px): ~100px mini player + bottom tab bar
  // On iOS, also account for safe-area-inset-bottom (home indicator area)
  &--has-player {
    .app-layout__center {
      // Desktop: 88px player + 8px buffer
      padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px));
    }

    .app-layout__right {
      // Reduce max-height to account for audio player (88px + 8px buffer)
      max-height: calc(100vh - var(--header-height) - 96px - env(safe-area-inset-bottom, 0px));
    }
  }
}

// Mid-desktop player padding (compact single-row layout)
@media (max-width: 1700px) {
  .app-layout--has-player {
    .app-layout__center {
      // Compact player is 56px, add 8px buffer
      padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
    }

    .app-layout__right {
      // Reduce max-height to account for compact audio player (56px + 8px buffer)
      max-height: calc(100vh - var(--header-height) - 64px - env(safe-area-inset-bottom, 0px));
    }
  }
}

// Page transition
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.2s var(--ease-out-expo);
}

.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}

// Modal overlay
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000; // Above everything (header: 3002, sidebar overlay: 3003, sidebar: 3004)
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-overlay), 0.7);
  backdrop-filter: blur(4px);
  padding: 24px;

  &--fullscreen {
    padding: 0;
    align-items: flex-start;
  }
}

.modal-container {
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px); // Dynamic viewport height for iOS Safari
  overflow-y: auto;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 20px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
}

// Settings modal
.settings-modal {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: var(--font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 50%;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.1);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__content {
    padding: 24px;
  }
}

// Settings sections
.settings-section {
  &__title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0 0 4px;
  }

  &__desc {
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin: 0 0 16px;
  }
}

.settings-divider {
  height: 1px;
  margin: 24px 0;
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.settings-theme-toggle {
  display: flex;
  gap: 8px;
}

.settings-theme-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  background: rgba(var(--v-theme-on-surface), 0.02);
  border-radius: 12px;
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-color: rgba(var(--v-theme-on-surface), 0.15);
    color: rgb(var(--v-theme-on-surface));
  }

  &--active {
    background: linear-gradient(
      135deg,
      rgba(var(--v-theme-primary), 0.15),
      rgba(var(--v-theme-primary), 0.05)
    );
    border-color: rgba(var(--v-theme-primary), 0.3);
    color: rgb(var(--v-theme-primary));
  }
}

.settings-token {
  display: flex;
  gap: 8px;

  &__input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 10px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    color: rgb(var(--v-theme-on-surface));
    outline: none;
    transition: all 0.2s ease;

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.35);
    }

    &:focus {
      border-color: rgba(var(--v-theme-primary), 0.4);
      box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.1);
    }
  }

  &__btn {
    padding: 12px 20px;
    border: none;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary))
    );
    border-radius: 10px;
    font-family: var(--font-body);
    font-size: 0.875rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-primary));
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.settings-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 10px 14px;
  background: rgba(var(--v-theme-error), 0.1);
  border: 1px solid rgba(var(--v-theme-error), 0.2);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: rgb(var(--v-theme-error));
}

.settings-api-url {
  padding: 12px 16px;
  background: rgba(var(--v-theme-on-surface), 0.03);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  border-radius: 10px;

  code {
    font-family: "SF Mono", "Consolas", monospace;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
}

// Search modal
.search-modal {
  width: 100%;
  height: 100%;
  background: rgb(var(--v-theme-surface));
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
    background: rgba(var(--v-theme-surface), 0.8);
    backdrop-filter: blur(10px);
  }

  &__back,
  &__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: none;
    color: rgba(var(--v-theme-on-surface), 0.5);
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
      color: rgb(var(--v-theme-on-surface));
    }
  }

  &__input {
    flex: 1;
    padding: 10px 0;
    border: none;
    background: none;
    font-family: var(--font-body);
    font-size: 1rem;
    color: rgb(var(--v-theme-on-surface));
    outline: none;

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.4);
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 16px 0;
    overflow-y: auto;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 48px 24px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.875rem;
  }

  &__suggestions {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
  }

  &__suggestion {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    border: none;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 12px;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.08);
    }

    .v-icon {
      color: rgba(var(--v-theme-on-surface), 0.5);
      flex-shrink: 0;
    }
  }

  &__suggestion-content {
    flex: 1;
    min-width: 0;
  }

  &__suggestion-text {
    display: block;
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__suggestion-subtext {
    display: block;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin-top: 2px;
  }

  &__hint {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    font-size: 0.9375rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
    text-align: center;
    padding: 48px 24px;
  }
}

// Modal transition
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s var(--ease-out-expo);

  .modal-container,
  .search-modal {
    transition: all 0.3s var(--ease-out-expo);
  }
}

.modal-enter-from,
.modal-leave-to {
  background: rgba(0, 0, 0, 0);

  .modal-container {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }

  .search-modal {
    opacity: 0;
    transform: translateY(-20px);
  }
}

// Light theme overrides
.v-theme--light {
  .app-layout {
    &__center {
      border-color: rgba(0, 0, 0, 0.06);
      background: rgba(255, 255, 255, 0.4);
    }
  }

  .modal-overlay {
    background: rgba(var(--v-theme-overlay), 0.4);
  }

  // Modal container uses CSS variables - no light theme override needed
}

// Responsive adjustments
@media (max-width: 1280px) {
  .app-layout {
    max-width: 100%;

    &__center {
      border-right: none;
    }
  }
}

@media (max-width: 960px) {
  .app-layout {
    &__center {
      border-left: none;
    }
  }
}

// Mobile-specific player padding
// Mini player sits above the bottom tab bar, so padding = tab bar (56px) + mini player (~100px)
// On mobile browsers (iOS Safari & Android Chrome), also account for browser chrome height
@media (max-width: 768px) {
  .app-layout--has-player {
    .app-layout__center {
      // Bottom tab bar (56px) + mini player (~100px) + small buffer
      // Safe area is already handled by the bottom tab bar position
      // Browser chrome height is added to ensure content isn't hidden behind chrome
      padding-bottom: calc(56px + 108px + env(safe-area-inset-bottom, 0px) + var(--mobile-browser-chrome-height, 0px));
    }
  }
}

// Center layout on desktop/tablet
@media (min-width: 768px) {
  .app-layout__center {
    max-height: calc(100vh - var(--header-height));
    overflow-y: auto;
  }
}


// Portrait-only overlay for mobile landscape
.landscape-overlay {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgb(var(--v-theme-surface));
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  color: rgb(var(--v-theme-on-surface));
  text-align: center;
  padding: 24px;

  &__icon {
    font-size: 64px;
    animation: rotate-phone 2s ease-in-out infinite;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
  }

  &__message {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin: 0;
  }
}

@keyframes rotate-phone {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-15deg);
  }
  75% {
    transform: rotate(15deg);
  }
}

// App loading screen - prevents FOUC by blocking DOM until settings load
.app-loading {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: rgb(var(--v-theme-background));
  padding: 24px;

  &__spinner {
    width: 48px;
    height: 48px;
    border: 3px solid rgba(var(--v-theme-on-background), 0.1);
    border-top-color: rgb(var(--v-theme-primary));
    border-radius: 50%;
    animation: app-loading-spin 0.8s linear infinite;
  }

  &__text {
    font-size: 0.9375rem;
    color: rgba(var(--v-theme-on-background), 0.6);
    margin: 0;
  }

  // Error state
  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
    max-width: 400px;
  }

  &__error-icon {
    opacity: 0.8;
  }

  &__error-title {
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-background));
    margin: 0;
  }

  &__error-message {
    font-size: 0.9375rem;
    color: rgba(var(--v-theme-on-background), 0.6);
    margin: 0;
    line-height: 1.5;
  }

  &__retry-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 140px;
    padding: 14px 28px;
    margin-top: 8px;
    border: none;
    background: linear-gradient(
      135deg,
      rgb(var(--v-theme-primary)),
      rgb(var(--v-theme-secondary, var(--v-theme-primary)))
    );
    border-radius: 12px;
    font-family: var(--font-body);
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-primary));
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  &__retry-spinner {
    animation: app-loading-spin 0.8s linear infinite;
  }
}

@keyframes app-loading-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
